import { useNavigate, useSearchParams } from 'react-router-dom';

import { getHMTenantSelection } from '@/modules/hiring-manager/utils/tenant';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

/** What a new chat's page reads from the navigation that opened it: the first message, for the page to send. */
export interface NewChatLocationState {
  start?: { text: string; contextJobId?: string };
}

/**
 * Opens a new chat with its first message. The chat's ID is chosen here, so its page opens at once and sends the
 * message itself (`useChatStartOnOpen`), instead of waiting for Finlay's reply to learn where the chat is.
 */
export const useNewChat = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUserQuery = useCurrentUserQuery();
  const { tenantSearch } = getHMTenantSelection(
    currentUserQuery.data,
    searchParams,
  );

  return (text: string, contextJobId?: string) => {
    const open = () =>
      navigate(`/hm/chat/${crypto.randomUUID()}${tenantSearch}`, {
        state: { start: { text, contextJobId } } satisfies NewChatLocationState,
      });
    if (!document.startViewTransition) return open();
    // The message box glides into the chat it opens (index.css). React Router drops `viewTransition` for navigations
    // inside HMApp's nested <Routes>, so the transition starts here and ends once the chat has rendered
    // (`chatOpened`), or after a moment, so a chat that never renders can't freeze the page.
    document.startViewTransition(
      () =>
        new Promise<void>((resolve) => {
          finishOpening = resolve;
          setTimeout(resolve, OPENING_TIMEOUT_MS);
          open();
        }),
    );
  };
};

const OPENING_TIMEOUT_MS = 500;
let finishOpening: (() => void) | null = null;

/** The chat `useNewChat` opened has rendered: its view transition can play. */
export const chatOpened = () => {
  finishOpening?.();
  finishOpening = null;
};
